import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { AuthState, User, LoginResponse } from '../types/auth';
import authService from '../services/authService';
import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '../api/client';

// Initial state
const initialState: AuthState = {
    user: null,
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

// Async thunk for login
export const loginUser = createAsyncThunk<
    { user: User; tokens: LoginResponse },
    { username: string; password: string },
    { rejectValue: string }
>('auth/login', async ({ username, password }, { rejectWithValue }) => {
    try {
        const tokens = await authService.login(username, password);
        const user = await authService.getCurrentUser();
        return { user, tokens };
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            const axiosError = error as { response?: { data?: { detail?: string } } };
            return rejectWithValue(
                axiosError.response?.data?.detail || 'Login failed'
            );
        }
        return rejectWithValue('Login failed');
    }
});

// Async thunk for initializing auth from stored tokens
export const initializeAuth = createAsyncThunk<
    User,
    void,
    { rejectValue: string }
>('auth/initialize', async (_, { rejectWithValue }) => {
    try {
        // getCurrentUser goes through apiClient, whose interceptor transparently
        // refreshes an expired access token once. If the refresh token is also
        // expired, the interceptor clears the session and redirects to login, so
        // there is no second refresh attempt to make here — the request simply
        // rejects and we treat the session as gone.
        const user = await authService.getCurrentUser();
        return user;
    } catch (error: unknown) {
        authService.logout();
        return rejectWithValue('Session expired. Please login again.');
    }
});

// Async thunk for logout
export const logoutUser = createAsyncThunk('auth/logout', async () => {
    authService.logout();
});

// Auth slice
export const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        setCredentials: (
            state,
            action: PayloadAction<{
                user: User;
                accessToken: string;
                refreshToken: string;
            }>
        ) => {
            state.user = action.payload.user;
            state.accessToken = action.payload.accessToken;
            state.refreshToken = action.payload.refreshToken;
            state.isAuthenticated = true;
            state.error = null;

            // Persist tokens to localStorage
            localStorage.setItem(ACCESS_TOKEN_KEY, action.payload.accessToken);
            localStorage.setItem(REFRESH_TOKEN_KEY, action.payload.refreshToken);
        },
        logout: (state) => {
            state.user = null;
            state.accessToken = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.error = null;

            // Clear tokens from localStorage
            localStorage.removeItem(ACCESS_TOKEN_KEY);
            localStorage.removeItem(REFRESH_TOKEN_KEY);
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload.user;
                state.accessToken = action.payload.tokens.access_token;
                state.refreshToken = action.payload.tokens.refresh_token;
                state.isAuthenticated = true;
                state.error = null;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Login failed';
                state.isAuthenticated = false;
            });

        // Initialize auth
        builder
            .addCase(initializeAuth.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(initializeAuth.fulfilled, (state, action) => {
                state.isLoading = false;
                state.user = action.payload;
                state.isAuthenticated = true;
                state.error = null;
            })
            .addCase(initializeAuth.rejected, (state) => {
                state.isLoading = false;
                state.user = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                // Don't set error for initialization failures
                // as they're expected when not logged in
            });

        // Logout
        builder
            .addCase(logoutUser.fulfilled, (state) => {
                state.user = null;
                state.accessToken = null;
                state.refreshToken = null;
                state.isAuthenticated = false;
                state.error = null;
            });
    },
});

// Export actions
export const { setCredentials, logout, setLoading, setError, clearError } =
    authSlice.actions;

// Export reducer
export default authSlice.reducer;
